/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button, Combobox, createTheme, Divider, Input, MantineColorsTuple, MantineTheme, Menu, MultiSelect, NavLink, Popover, Select, Text } from '@mantine/core';
import cn from '../utils/styleHelpers';

// Custom colors
const mainColors: MantineColorsTuple = [
  '#FBF7FC',
  '#EDE9FE',
  '#DDD6FE',
  '#C4B5FD',
  '#A78BFA',
  '#8B5CF6',
  '#7C3AED',
  '#6D28D9',
  '#5B21B6',
  '#4C1D95',
];

const textColors: MantineColorsTuple = [
  "#F3F4F6",
  "#E5E7EB",
  "#D1D5DB",
  "#9CA3AF",
  "#6B7280",
  "#4B5563",
  "#374151",
  "#1F2937",
  "#111827",
  "#111827", // Repeated last color to make it 10 elements
];

const theme = createTheme({
  fontFamily: 'Nunito',
  fontFamilyMonospace: 'Nunito',
  primaryShade: 3,
  colors: {
    main: mainColors,
    text: textColors,
    // Add Mantine's default emerald palette for the green buttons
    emerald: [
      '#ebfef6',
      '#ddf9ea',
      '#c0f2d2',
      '#a1e9b8',
      '#87e0a1',
      '#73d991',
      '#66d487',
      '#56b975',
      '#499f64',
      '#368252',
    ],
  },
  fontSizes: {
    xs: '0.6875rem', // 11px
    sm: '0.8125rem', // 13px
    md: '0.9375rem', // 15px
  },
  primaryColor: 'main',
  defaultRadius: 'md',

  // Advanced button theming with Tailwind and Mantine
  components: {
    Text: Text.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        root: cn(
          // Base styles for all buttons
          'leading-6',
          {
            'leading-1/2': props.size === 'xs' || props.size === 'sm',
          }
        ),
      }),
    }),
    NavLink: NavLink.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        root: cn(
          // Base styles for all buttons
          'focus:outline-none transition-colors py-1.5 font-semibold rounded-md text-gray-800',
        ),
      }),
    }),
    Button: Button.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        root: cn(
          // Base styles for all buttons
          'focus:outline-none transition-colors border rounded-md px-2',
          // Variant styles
          {
            // Green button variant (our custom variant)
            'bg-emerald-600 text-white hover:bg-emerald-700 focus:bg-emerald-600 focus:shadow-textBox focus:shadow-emerald-100': props.variant === 'green',
            
            // White button variant (our custom variant)
            'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-secondary-700 focus:bg-white focus:text-primary-600 focus:border-primary-600 focus:shadow-textBox': props.variant === 'white' || props.variant === 'subtle' || props.color === 'white',
            
            // Light variant (primary color border with light background)
            'border-primary-500 bg-primary-50 text-primary-700 hover:bg-primary-100 hover:text-primary-800 focus:bg-white focus:text-primary-700 focus:border-primary-600 focus:shadow-textBox': props.variant === 'light',
            
            // Purple button variant (our custom variant)
            'bg-primary-600 font-bold font-nunito text-white hover:bg-primary-700 focus:bg-primary-600 focus:outline-none focus:shadow-textBox disabled:bg-gray-400': props.variant === 'purple' || props.variant === 'default' || (!props.variant && !props.color),
            
            // Opaque button variant (our custom variant)
            'border-purple-500 bg-none text-purple-500 hover:text-purple-600 hover:border-purple-600 hover:bg-gray-50 focus:bg-white focus:text-primary-600 focus:border-primary-600 focus:shadow-textBox': props.variant === 'opaque',
          },
          {
           'px-3 py-0 text-sm': props.size === 'sm',
           'px-2 text-xs': props.size === 'xs',
          }
        ),
      }),
    }),
    MultiSelect: MultiSelect.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        dropdown: cn(
          'bg-gray-100/50 backdrop-blur-md shadow-dropdown',
        ),
        option: cn(

          // Base styles for all buttons
          'focus:outline-none transition-colors  text-xs p-3 py-2 hover:bg-gray-200 [data-combobox-selected="true"]:bg-primary-200',
        ),
        pillsList: cn(
          'flex flex-wrap gap-1',
        ),
        pill: cn(
          'border border-primary-200 text-xs bg-white text-primary-600',
        ),
        input: cn('flex items-center')
       
      }),
    }),
    Divider: Divider.extend({
      defaultProps: {
        color: 'rgba(61, 60, 60, 0.1)',
        variant: 'solid',
      },
    }),
    Select: Select.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        dropdown: cn(
          'bg-gray-100/50 backdrop-blur-md shadow-dropdown',
        ),
        option: cn(

          // Base styles for all buttons
          'focus:outline-none transition-colors  text-xs p-3 py-2 hover:bg-gray-200 [data-combobox-selected="true"]:bg-primary-200',
        ),
        input: cn(
          // Base styles for all buttons
          'focus:outline-none border hover:bg-secondary-100 hover:text-secondary-700 focus:bg-white focus:text-primary-600 focus:border-primary-600 focus:shadow-textBox border-gray-300 bg-gray-50 text-gray-600 transition-colors hover:bg-gray-100 py-0 border rounded-md',
          {
            'px-2 text-sm': props.size === 'sm',
            'px-2 text-xs h-4': props.size === 'xs',
          }
        ),
      }),
    }),
    Input: Input.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        input: cn(
          // Base styles for all buttons
          'focus:outline-none border hover:bg-secondary-100 hover:text-secondary-700 focus:bg-white focus:text-primary-600 focus:border-primary-600 focus:shadow-textBox border-gray-300 bg-gray-50 text-gray-600 transition-colors hover:bg-gray-100 py-0 border rounded-md',
        ),
      })
    }),
    Combobox: Combobox.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        dropdown: cn(
          'bg-gray-100/50 backdrop-blur-md shadow-dropdown',
        ),
        option: cn(

          // Base styles for all buttons
          'focus:outline-none transition-colors  text-xs p-3 py-2 hover:bg-gray-200 [data-combobox-selected="true"]:bg-primary-200',
        ),
        input: cn(
          // Base styles for all buttons
          'focus:outline-none text-gray-600 rounded-md border border-gray-300 border hover:bg-secondary-100 hover:text-secondary-700 focus:bg-white focus:text-primary-600 focus:border-primary-600 focus:shadow-textBox border-gray-300 bg-primary-50 text-gray-600 transition-colors hover:bg-gray-100 py-0 border rounded-md',
          {
            'px-2 text-sm': props.size === 'sm',
            'px-2 text-xs h-4': props.size === 'xs',
          }
        ),
      }),
    }),
    Popover: Popover.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        dropdown: cn(
          'bg-gray-100/50 backdrop-blur-md shadow-dropdown',
        ),
      }),
    }),
    Menu: Menu.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        dropdown: cn(
          'bg-gray-100/50 backdrop-blur-md shadow-dropdown',
        ),
        item: cn(

          // Base styles for all buttons
          'focus:outline-none transition-colors  text-xs p-3 py-2 hover:bg-gray-200',
        ),
      }),
    }),
  },

});

export default theme;
